import {
  blupayApi,
  getApiError,
} from "../api/client";

/**
 * meService
 *
 * Covers all /api/v1/me/* customer endpoints.
 *
 * Every request failure is normalized into BluPayApiError before it reaches
 * hooks/components. Customer screens should therefore use:
 *
 * error.message
 * error.action
 * error.code
 * error.retryable
 * error.requestId
 * error.fields
 */

async function unwrap(request) {
  try {
    const { data } =
      await request;

    return data;
  } catch (error) {
    throw getApiError(error);
  }
}

export const meService = {
  // ─── Profile ──────────────────────────────────────────────────────────────

  async getMe() {
    return unwrap(
      blupayApi.get("/me/")
    );
  },

  async updateMe(payload) {
    return unwrap(
      blupayApi.patch(
        "/me/",
        payload
      )
    );
  },

  // ─── KYC ──────────────────────────────────────────────────────────────────

  async getKycProfiles() {
    return unwrap(
      blupayApi.get(
        "/me/kyc-profiles"
      )
    );
  },

  // ─── Recipients ───────────────────────────────────────────────────────────

  async listRecipients() {
    return unwrap(
      blupayApi.get(
        "/me/recipients"
      )
    );
  },

  async getRecipient(
    recipientId
  ) {
    return unwrap(
      blupayApi.get(
        `/me/recipients/${recipientId}`
      )
    );
  },

  async createRecipient(
    payload
  ) {
    return unwrap(
      blupayApi.post(
        "/me/recipients",
        payload
      )
    );
  },

  async updateRecipient(
    recipientId,
    payload
  ) {
    return unwrap(
      blupayApi.patch(
        `/me/recipients/${recipientId}`,
        payload
      )
    );
  },

  async listPaymentMethods(
    recipientId
  ) {
    return unwrap(
      blupayApi.get(
        `/me/recipients/${recipientId}/payment-methods`
      )
    );
  },

  async createPaymentMethod(
    recipientId,
    payload
  ) {
    return unwrap(
      blupayApi.post(
        `/me/recipients/${recipientId}/payment-methods`,
        payload
      )
    );
  },

  // ─── Transfers ────────────────────────────────────────────────────────────

  async listTransfers({
    limit = 50,
    offset = 0,
  } = {}) {
    return unwrap(
      blupayApi.get(
        "/me/transfers",
        {
          params: {
            limit,
            offset,
          },
        }
      )
    );
  },

  async createTransfer(
    payload
  ) {
    return unwrap(
      blupayApi.post(
        "/me/transfers",
        payload
      )
    );
  },

  async getTransfer(
    transferId
  ) {
    return unwrap(
      blupayApi.get(
        `/me/transfers/${transferId}`
      )
    );
  },

  async getTransferDetail(
    transferId
  ) {
    return unwrap(
      blupayApi.get(
        `/me/transfers/${transferId}/detail`
      )
    );
  },

  async getFundingInstructions(
    transferId
  ) {
    return unwrap(
      blupayApi.get(
        `/me/transfers/${transferId}/funding-instructions`
      )
    );
  },

  async getCardOnramp(
    transferId
  ) {
    return unwrap(
      blupayApi.get(
        `/me/transfers/${transferId}/card-onramp`
      )
    );
  },

  async recordCardOnrampSubmission(
    transferId,
    status
  ) {
    return unwrap(
      blupayApi.post(
        `/me/transfers/${transferId}/card-onramp/submission`,
        { status }
      )
    );
  },

  async createQuote(
    transferId
  ) {
    return unwrap(
      blupayApi.post(
        `/me/transfers/${transferId}/quote`
      )
    );
  },

  async acceptQuote(
    transferId,
    quoteId
  ) {
    return unwrap(
      blupayApi.post(
        `/me/transfers/${transferId}/quotes/${quoteId}/accept`
      )
    );
  },

  async confirmTransfer(
    transferId,
    pin
  ) {
    return unwrap(
      blupayApi.post(
        `/me/transfers/${transferId}/confirm`,
        { pin }
      )
    );
  },

  async prepareTransfer(
    transferId,
    pin
  ) {
    return unwrap(
      blupayApi.post(
        `/me/transfers/${transferId}/prepare`,
        { pin }
      )
    );
  },

  // ─── Reference data ───────────────────────────────────────────────────────

  async getPayoutBanks(
    country
  ) {
    return unwrap(
      blupayApi.get(
        "/me/reference/banks",
        {
          params: {
            country,
          },
        }
      )
    );
  },
};